import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { AnalyticService, NotifyService } from '@app/services';
import { UpdateView } from '@common/views';
import { TimeOffApprovalFlowStep } from '@models/time-off-v3/time-off-approval-flow-step.model';
import { TimeOffApprovalFlow } from '@models/time-off-v3/time-off-approval-flow.model';
import { TimeOffApprovalObserver } from '@models/time-off-v3/time-off-approval-observer.model';
import { TimeOffApprover } from '@models/time-off-v3/time-off-approver.model';
import { ApprovalFlowOverallSettingsForm, ApprovalFlowStepsForm, ApprovalObserversForm } from '@time-off-v3/forms';

@Component({
    templateUrl: './approval-flow-update.template.html',
    styleUrls: ['./approval-flow-update.style.scss'],
})
export class ApprovalFlowUpdateView extends UpdateView {
    @ViewChild('approvalFlowOverallSettingsForm') approvalFlowOverallSettingsForm: ApprovalFlowOverallSettingsForm;
    @ViewChild('approvalFlowStepsForm') approvalFlowStepsForm: ApprovalFlowStepsForm;
    @ViewChild('approvalObserversForm') approvalObserversForm: ApprovalObserversForm;
    timeOffApprovalFlow: TimeOffApprovalFlow = new TimeOffApprovalFlow();
    isLoading = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private analyticService: AnalyticService
    ) {
        super();
        this.timeOffApprovalFlow = this.route.snapshot.data.models.get('approval-flow');
    }

    async onSave(): Promise<void> {
        this.approvalFlowOverallSettingsForm.submit();

        if (!this.approvalFlowOverallSettingsForm.valid) {
            return;
        }

        const approvalFlowPromise = this.timeOffApprovalFlow.save();

        this.analyticService.trackEvent(AnalyticEvents.TimeOffEditApprovalFlow);

        const approvalObserversPromises = this.approvalObserversForm.approvalObservers.map(
            (approvalObserver: TimeOffApprovalObserver) => approvalObserver.save()
        );

        const approvalObserversDeletionPromises = this.approvalObserversForm.deletedApprovalObservers.map(
            (approvalObserver: TimeOffApprovalObserver) => approvalObserver.delete()
        );

        // If we have automatic approval flow we do not need to take any actions for the steps
        if (this.timeOffApprovalFlow.isAutomatic) {
            await this.processPromises([
                approvalFlowPromise,
                ...approvalObserversPromises,
                ...approvalObserversDeletionPromises,
            ]);
            return;
        }

        const approvalFlowStepDeletionPromises = this.approvalFlowStepsForm.deletedApprovalFlowSteps.map(
            (approver: TimeOffApprovalFlowStep) => approver.delete()
        );

        const approversPromises = this.approvalFlowStepsForm.approvalFlowSteps.map(
            async (approvalFlowStep: TimeOffApprovalFlowStep, step: number) => {
                approvalFlowStep.stepIndex = step + 1;
                const savedApprovalFlowStep = await approvalFlowStep.save();

                // If we don't have approverId it means it's not specific person type and we are done here
                if (!approvalFlowStep.approverId) {
                    return;
                }

                // Create new approver
                if (!approvalFlowStep.hasApprover()) {
                    return new TimeOffApprover({
                        timeOffApprovalFlowStepId: savedApprovalFlowStep.id,
                        approverId: approvalFlowStep.approverId,
                    }).save();
                }

                // Update an approver
                approvalFlowStep.timeOffApprover.approverId = approvalFlowStep.approverId;
                return approvalFlowStep.timeOffApprover.save();
            }
        );

        await this.processPromises([
            approvalFlowPromise,
            ...approvalFlowStepDeletionPromises,
            ...approversPromises,
            ...approvalObserversPromises,
            ...approvalObserversDeletionPromises,
        ]);
    }

    async onCancel(): Promise<void> {
        await this.router.navigate(['../../../settings'], { relativeTo: this.route });
    }

    private async processPromises(promises: Promise<any>[]): Promise<void> {
        this.isLoading = true;

        try {
            await Promise.all(promises);
            await this.router.navigate(['../../../settings'], { relativeTo: this.route });
            this.notify.success('time-off-v3.approvalFlowHasBeenUpdated');
        } catch (e) {
            this.notify.error(e.message);
        }

        this.isLoading = false;
    }
}
